// Match server-side Session.MEANINGFUL_MIN/MAX_MINUTES.
const MIN_MINUTES = 1;
const MAX_MINUTES = 240;

const DAY_MS = 24 * 60 * 60 * 1000;
const MILESTONE_TIERS = [1, 5, 10, 25, 50, 100, 250, 500];
const STREAK_MILESTONE_TIERS = [2, 4, 8, 12, 26, 52];

function startOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay()); // back up to Sunday (day 0)
  return d;
}

export function weekKey(date) {
  const d = startOfWeek(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function durationMinutes(session) {
  if (!session.session_start || !session.session_end) return null;
  const dur = (new Date(session.session_end) - new Date(session.session_start)) / 60000;
  return dur;
}

export function isMeaningfulCompleted(session) {
  if (session.status !== "completed" || !session.session_end) return false;
  const dur = durationMinutes(session);
  return dur !== null && dur >= MIN_MINUTES && dur <= MAX_MINUTES;
}

export function filterCompleted(sessions) {
  return sessions.filter(isMeaningfulCompleted);
}

export function countUnfinished(sessions) {
  return sessions.filter(
    (s) => s.status === "abandoned" || s.status === "in_progress",
  ).length;
}

// Walk back day-by-day so DST transitions don't drift the boundary.
function previousWeekKey(currentWeekStart) {
  const prior = new Date(currentWeekStart);
  prior.setDate(prior.getDate() - 7);
  return weekKey(prior);
}

export function computeStreaks(completedSessions, now = new Date()) {
  const weeks = new Set(completedSessions.map((s) => weekKey(s.session_start)));

  // Longest streak: sort weeks ascending and find the longest run of weeks
  // that are exactly 7 days apart.
  const sortedStarts = Array.from(weeks)
    .map((k) => {
      const [y, m, d] = k.split("-").map(Number);
      return new Date(y, m - 1, d);
    })
    .sort((a, b) => a - b);

  let longest = 0;
  let run = 0;
  let prev = null;
  for (const ws of sortedStarts) {
    if (prev && Math.round((ws - prev) / DAY_MS) === 7) {
      run += 1;
    } else {
      run = 1;
    }
    if (run > longest) longest = run;
    prev = ws;
  }

  // Current streak: start at this week. If it has no session, drop back to
  // last week (grace period: streak isn't broken until the *next* week starts
  // without a session). Then walk backward while consecutive weeks are present.
  const thisWeek = startOfWeek(now);
  let cursorKey = weekKey(thisWeek);
  let cursorStart = thisWeek;
  if (!weeks.has(cursorKey)) {
    cursorKey = previousWeekKey(cursorStart);
    const prior = new Date(cursorStart);
    prior.setDate(prior.getDate() - 7);
    cursorStart = prior;
    if (!weeks.has(cursorKey)) {
      return { current: 0, longest, weeksPracticed: weeks.size };
    }
  }

  let current = 0;
  while (weeks.has(cursorKey)) {
    current += 1;
    cursorKey = previousWeekKey(cursorStart);
    const prior = new Date(cursorStart);
    prior.setDate(prior.getDate() - 7);
    cursorStart = prior;
  }

  return { current, longest, weeksPracticed: weeks.size };
}

export function computeHeatmap(completedSessions, weeks = 26, now = new Date()) {
  const counts = new Map();
  for (const s of completedSessions) {
    const k = weekKey(s.session_start);
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  const result = [];
  const cursor = startOfWeek(now);
  cursor.setDate(cursor.getDate() - (weeks - 1) * 7);
  for (let i = 0; i < weeks; i += 1) {
    const k = weekKey(cursor);
    result.push({
      weekKey: k,
      weekStart: new Date(cursor),
      count: counts.get(k) || 0,
    });
    cursor.setDate(cursor.getDate() + 7);
  }
  return result;
}

export function computeBestLift(completedSessions) {
  let best = null;
  for (const s of completedSessions) {
    if (s.mood_pre === null || s.mood_pre === undefined) continue;
    if (s.mood_post === null || s.mood_post === undefined) continue;
    const delta = s.mood_post - s.mood_pre;
    if (best === null || delta > best.delta) {
      best = {
        delta,
        sessionId: s.session_id,
        date: s.session_start,
        sessionType: s.session_type,
      };
    }
  }
  return best;
}

const TIME_BUCKETS = [
  { id: "morning", label: "Morning", start: 5, end: 12 },
  { id: "afternoon", label: "Afternoon", start: 12, end: 17 },
  { id: "evening", label: "Evening", start: 17, end: 21 },
  { id: "late", label: "Late night", start: 21, end: 29 }, // wraps past midnight
];

function bucketForHour(hour) {
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 21) return "evening";
  return "late";
}

export function computeMostUsedTimeOfDay(completedSessions) {
  if (completedSessions.length === 0) return null;
  const counts = { morning: 0, afternoon: 0, evening: 0, late: 0 };
  for (const s of completedSessions) {
    const hour = new Date(s.session_start).getHours();
    counts[bucketForHour(hour)] += 1;
  }
  let topId = null;
  let topCount = 0;
  for (const [id, c] of Object.entries(counts)) {
    if (c > topCount) {
      topCount = c;
      topId = id;
    }
  }
  if (!topId) return null;
  const meta = TIME_BUCKETS.find((b) => b.id === topId);
  return { id: topId, label: meta.label, count: topCount };
}

export function computeTotalMinutes(completedSessions) {
  let total = 0;
  for (const s of completedSessions) {
    const dur = durationMinutes(s);
    if (dur === null) continue;
    total += Math.max(MIN_MINUTES, Math.min(dur, MAX_MINUTES));
  }
  return Math.round(total);
}

export function computeMostUsedType(completedSessions) {
  if (completedSessions.length === 0) return null;
  const counts = {};
  for (const s of completedSessions) {
    const t = s.session_type || "Session";
    counts[t] = (counts[t] || 0) + 1;
  }
  let topType = null;
  let topCount = 0;
  for (const [t, c] of Object.entries(counts)) {
    if (c > topCount) {
      topCount = c;
      topType = t;
    }
  }
  return topType;
}

export function computeAvgMoodLift(completedSessions) {
  let count = 0;
  let pre = 0;
  let post = 0;
  for (const s of completedSessions) {
    if (s.mood_pre === null || s.mood_pre === undefined) continue;
    if (s.mood_post === null || s.mood_post === undefined) continue;
    pre += s.mood_pre;
    post += s.mood_post;
    count += 1;
  }
  if (count === 0) return null;
  return (post - pre) / count;
}

// Returns earned milestones only — never upcoming ones.
export function computeMilestones(completedSessions, longestStreak) {
  const sorted = completedSessions
    .filter((s) => s.session_end)
    .sort((a, b) => new Date(a.session_end) - new Date(b.session_end));
  const earned = [];

  for (const tier of MILESTONE_TIERS) {
    if (sorted.length >= tier) {
      const at = sorted[tier - 1];
      earned.push({
        id: `sessions-${tier}`,
        label: tier === 1 ? "First session" : `${tier} sessions`,
        dateEarned: at.session_end,
      });
    }
  }

  for (const tier of STREAK_MILESTONE_TIERS) {
    if (longestStreak >= tier) {
      earned.push({
        id: `streak-${tier}`,
        label: `${tier}-week streak`,
        dateEarned: null,
      });
    }
  }

  return earned;
}
